// ─────────────────────────────────────────────────────────────────────
// lib/personAvatar.js — avatar persona: lettura/salvataggio
//
// Dove vivono i dati: dim_user.avatar_settings (jsonb, già esistente), in una chiave
// annidata `person`, così non tocca le chiavi dell'avatar attuale (gender, patch, colori...).
//   avatar_settings = { ...avatar attuale, person: { gender, style, headwear, ... } }
// ─────────────────────────────────────────────────────────────────────
import { supabase } from './supabase'
import { PERSON_DEFAULT, PERSON_STYLES, PERSON_STYLES_BASE, PERSON_HEADWEAR, PERSON_JERSEYS, PERSON_BEARDS } from '../components/personAvatarConfig'

// Combinazioni naturali pelle / capelli / occhi (niente accostamenti improbabili): il default
// di ogni utente ne sceglie una in base al suo id. Stesso schema di VB.
const PRESETS = [
  { skin: '#F6DFCB', hair: '#D3B074', iris: '#5B8FC7' },
  { skin: '#F6DFCB', hair: '#5A3B26', iris: '#5B8FC7' },
  { skin: '#EDC7A2', hair: '#7B5535', iris: '#4F7A4A' },
  { skin: '#EDC7A2', hair: '#3B2A20', iris: '#5A3B26' },
  { skin: '#EDC7A2', hair: '#A8481E', iris: '#8A6A3B' },
  { skin: '#E0AC80', hair: '#33333A', iris: '#3B271C' },
  { skin: '#E0AC80', hair: '#5A3B26', iris: '#8A6A3B' },
  { skin: '#C68A5E', hair: '#33333A', iris: '#3B271C' },
  { skin: '#C68A5E', hair: '#3B2A20', iris: '#5A3B26' },
  { skin: '#C68A5E', hair: '#A98455', iris: '#8A6A3B' },
  { skin: '#A26640', hair: '#33333A', iris: '#3B271C' },
  { skin: '#A26640', hair: '#3B2A20', iris: '#5A3B26' },
  { skin: '#7A4B32', hair: '#33333A', iris: '#3B271C' },
  { skin: '#5A3828', hair: '#33333A', iris: '#3B271C' },
  { skin: '#43281D', hair: '#33333A', iris: '#3B271C' },
  { skin: '#EDC7A2', hair: '#33333A', iris: '#7F8F9B' },
]

// FNV-1a: hash stabile e veloce di una stringa → intero senza segno
function hash(str) {
  let h = 2166136261
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

// Default per utente: stesso id → sempre lo stesso avatar (nessuna scrittura su DB).
// ATTENZIONE: il risultato dipende dall'ORDINE di PRESETS e di PERSON_STYLES_BASE (non di PERSON_STYLES: i tagli h5..h9 sono
// scelte dell'utente e non entrano nel default). Cambiare ordine o numero degli elementi esistenti cambia l'avatar
// generato di tutti gli utenti che non l'hanno ancora personalizzato.
// Niente copricapo né occhiali: sono scelte da fare apposta. La maglia resta quella di default.
export function defaultPerson(userId) {
  const h = hash(String(userId ?? 'anon'))
  const gender = (h >>> 8) % 2 === 0 ? 'man' : 'woman'
  const styles = PERSON_STYLES_BASE[gender]
  return { gender, style: styles[(h >>> 12) % styles.length].key, ...PRESETS[h % PRESETS.length] }
}

const HEX = /^#[0-9A-Fa-f]{6}$/
const COLOR_KEYS = ['skin', 'hair', 'iris', 'main', 'accent', 'accent2', 'trim', 'wear', 'detail', 'frame', 'lens']

// Tiene solo valori validi: i dati arrivano dal DB
function sanitize(person) {
  const out = {}
  if (!person || typeof person !== 'object') return out
  if (person.gender === 'man' || person.gender === 'woman') out.gender = person.gender
  const g = out.gender ?? PERSON_DEFAULT.gender
  if (PERSON_STYLES[g].some(s => s.key === person.style)) out.style = person.style
  if (PERSON_HEADWEAR.some(w => w.key === person.headwear)) out.headwear = person.headwear
  if (typeof person.glasses === 'boolean') out.glasses = person.glasses
  if (g === 'man' && PERSON_BEARDS.some(b => b.key === person.beard)) {
    out.beard = person.beard
    if (typeof person.beardA === 'number' && person.beardA > 0 && person.beardA < 1) out.beardA = person.beardA
  }
  if (typeof person.face === 'string' && /^[a-g]$/.test(person.face)) out.face = person.face
  if (person.brow === 1 || person.brow === 2) out.brow = person.brow
  if (person.flip === true) out.flip = true
  if (g === 'woman' && person.fringe === true) out.fringe = true
  if (PERSON_JERSEYS.some(j => j.key === person.jersey)) out.jersey = person.jersey
  if (person.grad === 'low' || person.grad === 'mid') out.grad = person.grad
  for (const k of COLOR_KEYS) {
    if (typeof person[k] === 'string' && HEX.test(person[k])) out[k] = person[k]
  }
  return out
}

// Avatar finale: default generato dall'id (se noto) + personalizzazioni salvate in `person`
export function resolvePerson(avatarSettings, userId) {
  const base = userId ? defaultPerson(userId) : {}
  return { ...PERSON_DEFAULT, ...base, ...sanitize(avatarSettings?.person) }
}

export async function loadPerson(userId) {
  const { data, error } = await supabase.from('dim_user').select('avatar_settings').eq('id_user', userId).single()
  if (error) return { error }
  return { person: resolvePerson(data?.avatar_settings, userId), saved: !!data?.avatar_settings?.person }
}

// Salva (o rimuove, con person = null) l'avatar persona senza toccare le altre chiavi
export async function savePerson(userId, person) {
  const { data, error: readErr } = await supabase
    .from('dim_user').select('avatar_settings').eq('id_user', userId).single()
  if (readErr) return { error: readErr }
  const next = { ...(data?.avatar_settings ?? {}) }
  if (person) next.person = sanitize(person)
  else delete next.person
  const { error } = await supabase.from('dim_user').update({ avatar_settings: next }).eq('id_user', userId)
  return { error }
}
